/**
 * Error_Handler.ts (ported from SK Catalog's src/utils/errorHandler.ts)
 *
 * Single source of truth for extracting and displaying backend error messages.
 * Use handleApiError(error, title) in EVERY catch block instead of hand-parsing
 * error.response.data.message.
 *
 * Backend error shapes handled:
 *   Platform API (RFC 7807) → { title } | { detail }
 *   Adapter / most APIs     → { message } | { error: { message } } | { errors:[{message}] }
 *   Network / JS error      → Error.message
 */
import { showToast } from '../components/toastComponent';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/** Characters that are unsafe to display in a toast — blocks HTML/script injection. */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0 && !BLOCKED.test(val);
}

/**
 * Extracts the most specific backend error message from an axios/JS error.
 * Falls back to `fallback` only when no usable message is found.
 */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;

  const err = error as any;
  const data = err?.response?.data;

  if (isSafeString(data?.message)) return data.message.trim().slice(0, MAX_LENGTH);
  if (isSafeString(data?.error?.message)) return data.error.message.trim().slice(0, MAX_LENGTH);
  const firstError = Array.isArray(data?.errors) ? data.errors[0] : null;
  if (isSafeString(firstError?.message)) return firstError.message.trim().slice(0, MAX_LENGTH);
  if (isSafeString(data?.title)) return data.title.trim().slice(0, MAX_LENGTH);   // Platform RFC 7807
  if (isSafeString(data?.detail)) return data.detail.trim().slice(0, MAX_LENGTH);
  if (isSafeString(data?.error)) return data.error.trim().slice(0, MAX_LENGTH);
  if (isSafeString(err?.message)) return err.message.trim().slice(0, MAX_LENGTH);

  return fallback;
}

/**
 * Shows a backend error message in an error toast.
 *
 * @param error    - The caught error (any shape)
 * @param title    - Toast heading (e.g. 'Save failed', 'Booking failed')
 * @param fallback - Optional custom fallback when no backend message is found
 */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  showToast('error', title, getApiErrorMessage(error, fallback));
}
